import { Component, Input, Output, EventEmitter, Inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CustomerEntity } from '../../../models/entities/customer';
import { CUSTOMER_ENTITY_SERVICE_TOKEN } from '../../../injection-tokens/entity-service-tokens';
import { ICustomerEntityService } from '../../../interfaces/entities/customer-entity-service.interface';

@Component({
  selector: 'app-new-customer-drawer',
  templateUrl: './new-customer-drawer.component.html',
  styleUrl: './new-customer-drawer.component.css',
})
export class NewCustomerDrawerComponent {
  @Input() opened = false;
  @Input() customerNameTaken = false;
  @Input() customerNameChecking = false;
  @Input() allCustomers: CustomerEntity[] = [];

  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<CustomerEntity>();
  @Output() nameInputChange = new EventEmitter<string>();

  newCustomer: CustomerEntity = new CustomerEntity();

  constructor(
    private readonly snackBar: MatSnackBar,
    @Inject(CUSTOMER_ENTITY_SERVICE_TOKEN)
    private readonly customerService: ICustomerEntityService,
  ) {}

  closeDrawer(): void {
    this.closed.emit();
  }

  onCustomerNameInput(value: string): void {
    this.nameInputChange.emit(value);
  }

  saveNewCustomer(): void {
    const trimmedName = (this.newCustomer.name ?? '').trim();
    if (!trimmedName) return;

    this.newCustomer.name = trimmedName;
    const isDuplicate = this.isCustomerNameDuplicate(trimmedName);
    if (isDuplicate) {
      this.snackBar.open('Name already exists. Please choose a different customer name.', 'OK', {
        duration: 2500,
      });
      return;
    }

    this.customerService.createCustomer(this.newCustomer, '').subscribe({
      next: (saved: any) => {
        const entity = saved as CustomerEntity;
        this.snackBar.open(`Customer "${entity.name}" added.`, 'OK', { duration: 2500 });
        this.saved.emit(entity);
        this.newCustomer = new CustomerEntity();
        this.closed.emit();
      },
      error: () => {
        this.snackBar.open('Failed to save customer. Please try again.', 'Dismiss', {
          duration: 3000,
        });
      },
    });
  }

  isSaveButtonDisabled(): boolean {
    return (
      !this.newCustomer.name?.trim() ||
      this.customerNameTaken ||
      this.customerNameChecking
    );
  }

  private isCustomerNameDuplicate(value: string): boolean {
    const normalized = (value ?? '').trim().toLowerCase();
    if (!normalized) {
      return false;
    }

    return this.allCustomers.some(c => (c.name ?? '').trim().toLowerCase() === normalized);
  }
}
